import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

interface ModalProps {
	open: boolean;
	onClose: () => void;
	title?: string;
	children: ReactNode;
	widthClass?: string;
	hideHeader?: boolean;
}

/** Centered dialog with a blurred backdrop. Closes on Esc / backdrop click. */
export function Modal({
	open,
	onClose,
	title,
	children,
	widthClass = "max-w-md",
	hideHeader = false,
}: ModalProps) {
	const { t } = useTranslation();
	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
			role="dialog"
			aria-modal="true"
		>
			<button
				type="button"
				aria-label={t("common.close")}
				onClick={onClose}
				className="absolute inset-0 bg-black/60 backdrop-blur-sm"
			/>
			<div
				className={`glass animate-fade-up relative max-h-[calc(100dvh-2rem)] w-full ${widthClass} overflow-y-auto overscroll-contain rounded-2xl border border-line shadow-[var(--shadow-panel)]`}
			>
				{hideHeader ? null : (
					<div className="flex items-center justify-between px-5 pt-4">
						{title ? <h2 className="text-base font-semibold">{title}</h2> : <span aria-hidden="true" />}
						<button
							type="button"
							onClick={onClose}
							aria-label={t("common.close")}
							className="grid size-8 place-items-center rounded-full text-muted transition hover:bg-surface-2 hover:text-text"
						>
							<X size={18} />
						</button>
					</div>
				)}
				<div className="p-5">{children}</div>
			</div>
		</div>
	);
}
